/**
 * Get game box art image URL (Twitch-style)
 * Uses Twitch's CDN for game box art when available
 * 
 * Twitch box art CDN format:
 * https://static-cdn.jtvnw.net/ttv-boxart/{game-id}-{width}x{height}.jpg
 * 
 * However, we can also use the game name format:
 * https://static-cdn.jtvnw.net/ttv-boxart/{game-name}-{width}x{height}.jpg
 */

/**
 * Known game name mappings for Twitch box art
 * Maps our game names to Twitch's exact game IDs
 * These are the official Twitch game IDs from the Twitch API
 */
const gameNameMap = {
  // Counter-Strike 2
  'Counter-Strike 2': '32399',
  'Counter‑Strike 2': '32399', // Note: uses non-breaking hyphen
  'CS2': '32399',
  'Counter Strike 2': '32399',
  
  // Dota 2
  'Dota 2': '29595',
  'Dota2': '29595',
  'DOTA 2': '29595',
  
  // League of Legends
  'League of Legends': '21779',
  'LoL': '21779',
  'League Of Legends': '21779',
  
  // Rocket League
  'Rocket League': '30921',
  'RocketLeague': '30921',
  
  // Other popular games (for future use)
  'Valorant': '516575',
  'Fortnite': '33214',
  'Apex Legends': '511224',
  'Call of Duty': '488552',
  'Overwatch': '488552',
  'Overwatch 2': '515025',
};

/**
 * Get normalized game name for Twitch box art
 */
export function getNormalizedGameName(gameName) {
  // Check if we have a known mapping
  if (gameNameMap[gameName]) {
    return gameNameMap[gameName];
  }

  // Otherwise normalize the name (lowercase, replace spaces with hyphens)
  return gameName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Get game box art image URL from Twitch CDN
 * @param {string} gameName - The name of the game
 * @returns {string} - URL to the game box art image
 */
export function getGameImageUrl(gameName) {
  const normalizedName = getNormalizedGameName(gameName);
  
  // Twitch box art CDN format
  // Format: https://static-cdn.jtvnw.net/ttv-boxart/{game-id-or-name}-{width}x{height}.jpg
  // Common sizes: 
  // - 285x380 (small portrait)
  // - 600x800 (large portrait) 
  // - 1440x1920 (very large portrait)
  // For game cards with 16:9 aspect ratio, we use a larger size and let CSS crop it
  // Using 600x800 for good quality without being too large
  const twitchBoxArtUrl = `https://static-cdn.jtvnw.net/ttv-boxart/${normalizedName}-600x800.jpg`;

  return twitchBoxArtUrl;
}

/**
 * Get game box art with fallback
 * Returns the Twitch URL, browser will handle 404s with onError handler
 */
export function getGameImage(gameName) {
  return getGameImageUrl(gameName);
}
